/**
 * Calendar-date comparison for values that mean a date rather than an instant, such
 * as certification active and expiration dates.
 *
 * Stored bounds are read on their UTC calendar date, since that is how the API
 * encodes them. "Today" is read on the viewer's local calendar date. Both bounds are
 * inclusive.
 */

/** A calendar day as a comparable YYYYMMDD integer. */
export type CalendarDay = number;

/**
 * A date value as it reaches these helpers: an API string, a `Date`, or the Moment
 * the datepickers emit, since the app registers `MatMomentDateModule`.
 */
export type CalendarDateInput = Date | string | { toDate(): Date } | null | undefined;

const asCalendarDay = (year: number, monthIndex: number, day: number): CalendarDay =>
  year * 10000 + (monthIndex + 1) * 100 + day;

/** Normalizes any accepted input to a valid `Date`, or null. */
function toDate(value: CalendarDateInput): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'object' && typeof value.toDate === 'function') {
    date = value.toDate();
  } else {
    date = new Date(value as string);
  }

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

/**
 * The UTC calendar day of a stored date value. Returns undefined for null/empty or
 * unparseable input.
 */
export function toUtcCalendarDay(value: CalendarDateInput): CalendarDay | undefined {
  const date = toDate(value);
  if (date === null) {
    return undefined;
  }

  return asCalendarDay(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** The viewer's local calendar day. Defaults to today. */
export function toLocalCalendarDay(value: Date = new Date()): CalendarDay {
  return asCalendarDay(value.getFullYear(), value.getMonth(), value.getDate());
}

/**
 * The value to seed a datepicker control with, as local midnight of the day the API
 * encoded. Handing the raw API string to the control instead lets the Moment adapter
 * read it in local time, which shows the previous day west of Greenwich and saves
 * that day back.
 */
export function toDatePickerDate(value: CalendarDateInput): Date | null {
  const day = toUtcCalendarDay(value);
  if (day === undefined) {
    return null;
  }

  return new Date(Math.floor(day / 10000), (Math.floor(day / 100) % 100) - 1, day % 100);
}

/**
 * Serializes a date value as a date-only `YYYY-MM-DD` string for the API, so the
 * author's UTC offset is not stored as a time of day.
 */
export function toApiCalendarDate(value: CalendarDateInput): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = toDate(value);
  if (date === null) {
    return null;
  }

  // A string is an API payload the author never edited, so it keeps the UTC day it
  // was encoded with; anything else came from a datepicker and keeps the local day
  // that was clicked.
  return typeof value === 'string'
    ? formatCalendarDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    : formatCalendarDate(date.getFullYear(), date.getMonth(), date.getDate());
}

const formatCalendarDate = (year: number, monthIndex: number, day: number): string =>
  `${year}-${`${monthIndex + 1}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;

/**
 * Whether `today` falls inside [start, end], inclusive at both ends.
 *
 * A missing bound is treated as open: no expiration means it does not expire, and a
 * value with neither bound is active.
 */
export function isWithinCalendarDateRange(
  start: CalendarDateInput,
  end: CalendarDateInput,
  today: Date = new Date()
): boolean {
  const startDay = toUtcCalendarDay(start);
  const endDay = toUtcCalendarDay(end);
  const currentDay = toLocalCalendarDay(today);

  if (startDay !== undefined && currentDay < startDay) {
    return false;
  }

  if (endDay !== undefined && currentDay > endDay) {
    return false;
  }

  return true;
}
